import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Menu, X } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_de12da21-0869-4a9b-8efe-c2e2b4d72064/artifacts/km3uhq32_YGK-CONSTRUCTION.png";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/services", label: "Services" },
    { href: "/portfolio", label: "Portfolio" },
    { href: "/estimator", label: "Estimator" },
    { href: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-black/95 backdrop-blur-md shadow-lg border-b border-orange-500/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="YGK Construction" 
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium tracking-wide uppercase transition-colors duration-200 ${
                  isActive(link.href)
                    ? "text-orange-500"
                    : "text-white/80 hover:text-orange-400"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/estimator">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 btn-glow transition-all">
                Get Estimate
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-white" />
            ) : (
              <Menu className="h-6 w-6 text-white" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-md border-t border-orange-500/20 py-6">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-base font-medium px-4 py-2 ${
                    isActive(link.href)
                      ? "text-orange-500 bg-orange-500/10"
                      : "text-white/80 hover:text-orange-400"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="px-4 pt-4">
                <Link to="/estimator" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold">
                    Get Free Estimate
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-black text-white border-t border-orange-500/20">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <img 
              src={LOGO_URL} 
              alt="YGK Construction" 
              className="h-20 w-auto mb-4"
            />
            <p className="text-gray-400 leading-relaxed max-w-md">
              Your Good Karma Construction - Building Oklahoma City's future with quality craftsmanship, 
              transparent pricing, and a commitment to excellence since 2008.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-orange-500 mb-4">
              Quick Links
            </h4>
            <div className="flex flex-col gap-3">
              <Link to="/services" className="text-gray-400 hover:text-orange-400 transition-colors">
                Our Services
              </Link>
              <Link to="/portfolio" className="text-gray-400 hover:text-orange-400 transition-colors">
                Portfolio
              </Link>
              <Link to="/estimator" className="text-gray-400 hover:text-orange-400 transition-colors">
                Free Estimate
              </Link>
              <Link to="/contact" className="text-gray-400 hover:text-orange-400 transition-colors">
                Contact Us
              </Link>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-orange-500 mb-4">
              Contact
            </h4>
            <div className="flex flex-col gap-3 text-gray-400">
              <p>Oklahoma City, Oklahoma</p>
              <a href="tel:4052562176" className="hover:text-orange-400 transition-colors">
                (405) 256-2176
              </a>
              <a href="mailto:ygkconstruction@gmail.com" className="hover:text-orange-400 transition-colors">
                ygkconstruction@gmail.com
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} YGK Construction - Your Good Karma Construction. All rights reserved.
          </p>
          <p className="text-gray-500 text-sm">
            Proudly serving the Oklahoma City area
          </p>
        </div>
      </div>
    </footer>
  );
};
